/**
 * ModelDetailPage — the model home (TRA-868).
 *
 * One continuous scroll of hairline-separated bands rather than tabs:
 * training history, uptime, requests, evaluations + deployment, checkpoints,
 * activity, and the raw request log. Section components live in
 * components/models/home/.
 *
 * Every band is live. Training history and checkpoints are two views of
 * the same derived lineage (`/api/models/{id}/checkpoints`) — the distinct
 * checkpoints a pipeline scored on this model's eval surface; activity
 * composes those scores with the model's creation and the gateway's outage
 * probes. Usage numbers come from the gateway: traffic, tokens, latency
 * percentiles and cost from the D1 audit log, availability from the
 * two-tier probes. A team with no traffic renders the empty states.
 */

import { useMemo, useState } from 'react'
import { Navigate, Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { getApiErrorStatus } from '@/api/client'
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Star,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'

import { ChatSheet } from '@/components/models/ChatSheet'
import { useTeam } from '@/contexts/TeamContext'
import { DefaultModelBadge } from '@/components/models/DefaultModelBadge'
import { DeleteModelDialog } from '@/components/models/DeleteModelDialog'
import { ModelFormDialog } from '@/components/models/ModelFormDialog'
import { ApiAccessButtons } from '@/components/models/ApiAccessButtons'
import { RequestsTab } from '@/components/models/RequestsTab'
import {
  BasetenScalingSection,
  ReplicaCountBadge,
} from '@/components/models/home/BasetenConfigPanel'
import { BasetenScheduleSection } from '@/components/models/home/BasetenScheduleSection'
import { CheckpointsTable } from '@/components/models/home/CheckpointsTable'
import { DeploymentPanel } from '@/components/models/home/DeploymentPanel'
import { EvaluationsPanel } from '@/components/models/home/EvaluationsPanel'
import { EventsList } from '@/components/models/home/EventsList'
import { ModelHomeSection, SectionHeading } from '@/components/models/home/ModelHomeSection'
import { RequestsPanel } from '@/components/models/home/RequestsPanel'
import { ThroughputChart } from '@/components/models/home/ThroughputChart'
import { TrainingHistoryPanel } from '@/components/models/home/TrainingHistoryPanel'
import { UptimeGrid, UptimeLegend } from '@/components/models/home/UptimeGrid'
import type { RequestWindowKey } from '@/components/models/home/contracts'
import {
  initialLogView,
  type RequestLogView,
  withLogView,
} from '@/components/models/home/requestLogOptions'
import {
  lineageSubtitle,
  toRequestWindow,
  toServingStats,
  toThroughput,
  toUptimeGrid,
  uptimeMonthLabels,
  uptimeSummary,
} from '@/components/models/home/contracts'
import { ServingBadge } from '@/components/models/ServingBadge'
import { Badge } from '@/components/ui/badge'
import { CopyButton } from '@/components/ui/copy-button'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { PageContainer } from '@/components/ui/page-container'
import { Skeleton } from '@/components/ui/skeleton'
import { useCheckpoints, useModelActivity } from '@/hooks/useCheckpoints'
import { useEvalsForModel } from '@/hooks/useEvals'
import { useModelUptime, useModelUsageTimeseries } from '@/hooks/useUsage'
import {
  TEAM_SCOPE,
  useFallbackOptions,
  useModel,
  useSetDefaultModel,
  useTraceLogging,
} from '@/hooks/useModels'

/**
 * Whether the gateway stores request/response bodies for this model
 * (TRA-1009). Quiet while the state loads or fails; the switch on the
 * Models page is the write surface, this is just the read.
 */
function TraceLoggingBadge({
  gatewayModelId,
}: {
  gatewayModelId: string
}): React.JSX.Element | null {
  const traceState = useTraceLogging()
  if (traceState.data === undefined) return null
  const on =
    traceState.data.enabled &&
    !traceState.data.disabled_models.includes(gatewayModelId)
  return (
    <Badge
      variant="outline"
      title={
        on
          ? 'Request and response bodies are stored for this model'
          : 'Requests still flow and are counted, but bodies are not stored'
      }
    >
      {on ? 'Trace logging on' : 'Trace logging off'}
    </Badge>
  )
}

const WINDOW_LABELS: Record<RequestWindowKey, string> = {
  today: 'Today',
  week: '7-day',
  month: '30-day',
}

/** Zeroed tiles while the first request is in flight. */
const EMPTY_STATS = toServingStats({
  start: '',
  end: '',
  bucket: '30m',
  pricing_status: 'unavailable',
  points: [],
  previous_points: [],
})

export default function ModelDetailPage(): React.JSX.Element {
  const { modelId } = useParams<{ modelId: string }>()
  const navigate = useNavigate()
  const { isRestricted } = useTeam()
  const { data: model, isLoading, isError, error } = useModel(modelId)
  const evals = useEvalsForModel(modelId)
  const checkpoints = useCheckpoints(modelId)
  const activity = useModelActivity(modelId)
  const fallbackOptions = useFallbackOptions(modelId, TEAM_SCOPE)
  const setDefaultMutation = useSetDefaultModel(TEAM_SCOPE)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)

  const [window, setWindow] = useState<RequestWindowKey>('today')
  // The section heading follows the toggle; `initialLogView` says why a
  // deep link opens on the request log.
  const [searchParams, setSearchParams] = useSearchParams()
  const logView = initialLogView(searchParams)

  function setLogView(next: RequestLogView): void {
    setSearchParams((prev) => withLogView(prev, next), { replace: true })
  }

  function drillIntoFallbacks(): void {
    setSearchParams(
      (prev) => {
        const params = withLogView(prev, 'requests')
        params.set('fallback', '1')
        return params
      },
      { replace: true },
    )
    document.getElementById('request-log')?.scrollIntoView({ behavior: 'smooth' })
  }
  const timeseries = useModelUsageTimeseries(modelId, window)
  const uptimeQuery = useModelUptime(modelId)

  const stats = useMemo(
    () => (timeseries.data ? toServingStats(timeseries.data) : EMPTY_STATS),
    [timeseries.data],
  )
  const requests = useMemo(
    () =>
      timeseries.data
        ? toRequestWindow(WINDOW_LABELS[window], timeseries.data)
        : { label: WINDOW_LABELS[window], total: '0', buckets: [] },
    [timeseries.data, window],
  )
  const throughput = useMemo(
    () => (timeseries.data ? toThroughput(timeseries.data) : []),
    [timeseries.data],
  )

  if (isLoading) {
    return (
      <PageContainer className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    )
  }

  // A 404 means the model is not in the active team (team switch) or was
  // deleted: go to the section root, not a dead error page.
  if (isError && getApiErrorStatus(error) === 404) {
    return <Navigate to="/models" replace />
  }

  if (isError || !model) {
    return (
      <PageContainer>
        <p className="text-sm text-destructive">Failed to load model.</p>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <div className="border-b border-border pb-6">
        <Button variant="ghost" size="sm" asChild className="-ml-2 mb-3">
          <Link to="/models">
            <ArrowLeft className="mr-2 h-4 w-4" />
            All models
          </Link>
        </Button>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="font-serif text-2xl font-medium">{model.display_name}</h1>
              {/* The gateway's newest probe, not a decoration. This badge
                  used to read "Serving" unconditionally, which was a claim
                  the page had no basis for. */}
              {uptimeQuery.isError ? (
                // A failed lookup is not "not probed" — say the status is
                // unavailable instead of borrowing the unknown state,
                // which now renders as a quiet null dash.
                <span
                  title="The status request failed. Reload to retry"
                  className="font-mono text-[10px] uppercase tracking-wider text-destructive"
                >
                  status unavailable
                </span>
              ) : uptimeQuery.data ? (
                <ServingBadge status={uptimeQuery.data.status} />
              ) : (
                <Skeleton className="h-5 w-16" />
              )}
              {fallbackOptions.data?.forwards_to.upstream_id === 'baseten' && (
                <ReplicaCountBadge modelId={model.id} />
              )}
              <TraceLoggingBadge gatewayModelId={model.gateway_model_id} />
              {model.is_default && <DefaultModelBadge />}
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {model.gateway_model_id}
              </p>
              {/* The id clients actually send as `model` — the single most
                  copied string on this page. */}
              <CopyButton value={model.gateway_model_id} label="model id" />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* The metrics page (TRA-1461): every band below in finer
                detail, on one window picker. */}
            <Button variant="outline" asChild>
              <Link to={`/models/${model.id}/metrics`}>
                <Activity className="mr-2 h-4 w-4" />
                Metrics
              </Link>
            </Button>
            <Button onClick={() => { setChatOpen(true) }}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Playground
            </Button>
            {/* Restricted demo teams (TRA-832): the API menu stays (sample
                request is demo content — key minting is hidden inside it),
                edit/delete go. Playground stays too; the API 403s any
                non-employee mutation regardless. */}
            <ApiAccessButtons model={model} />
            {!isRestricted && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" aria-label="More actions">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {/* The nightly evals score the team's default (TRA-1215);
                        promoting a retrain is this one action. */}
                    {!model.is_default && (
                      <DropdownMenuItem
                        disabled={setDefaultMutation.isPending}
                        onSelect={() => {
                          setDefaultMutation.mutate(
                            { modelId: model.id },
                            { onSuccess: () => { toast.success('Default model set') } },
                          )
                        }}
                      >
                        <Star className="mr-2 h-4 w-4" />
                        Make default
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onSelect={() => { setEditOpen(true) }}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={() => { setConfirmOpen(true) }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Baseten-only band (TRA-1051). Quiet while the route loads or the
          lookup fails — most models are not Baseten-backed, and a skeleton
          here would flash on every one of them (same stance as
          TraceLoggingBadge above). */}
      {/* Dedicated fleet only: `baseten-serverless` is a shared catalog
          tier with nothing configurable per team. */}
      {fallbackOptions.data?.forwards_to.upstream_id === 'baseten' && (
        <>
          <BasetenScalingSection modelId={model.id} />
          <BasetenScheduleSection modelId={model.id} />
        </>
      )}

      {checkpoints.isLoading ? (
        <ModelHomeSection title="Training history">
          <Skeleton className="h-[280px] w-full" />
        </ModelHomeSection>
      ) : checkpoints.data && checkpoints.data.checkpoints.length > 0 ? (
        <ModelHomeSection
          title="Training history"
          subtitle={lineageSubtitle(checkpoints.data)}
        >
          <TrainingHistoryPanel checkpoints={checkpoints.data} />
        </ModelHomeSection>
      ) : null}

      <ModelHomeSection
        title="Uptime"
        subtitle={
          uptimeQuery.data
            ? uptimeSummary(uptimeQuery.data)
            : 'Last 90 days in 8-hour blocks'
        }
        action={<UptimeLegend />}
      >
        {/* A failed query is neither loading nor empty. Branching on `data`
            alone left this as a skeleton that never resolves — a page that
            looks busy forever rather than one that admits it failed. */}
        {uptimeQuery.isError ? (
          <p className="text-sm text-destructive">Failed to load availability.</p>
        ) : uptimeQuery.data ? (
          <UptimeGrid
            days={toUptimeGrid(uptimeQuery.data)}
            months={uptimeMonthLabels(uptimeQuery.data)}
            onFallbackClick={drillIntoFallbacks}
          />
        ) : (
          <Skeleton className="h-[45px] w-full" />
        )}
      </ModelHomeSection>

      <ModelHomeSection
        title="Requests"
        subtitle="Requests per minute through the inference gateway · deltas vs the preceding window"
      >
        {timeseries.isError ? (
          <p className="text-sm text-destructive">Failed to load gateway traffic.</p>
        ) : (
          <RequestsPanel
            stats={stats}
            data={requests}
            window={window}
            onWindowChange={setWindow}
            isLoading={timeseries.isLoading}
          >
            <ThroughputChart buckets={throughput} />
          </RequestsPanel>
        )}
      </ModelHomeSection>

      <section className="grid border-b border-border md:grid-cols-2">
        <div className="py-6 md:border-r md:border-border md:pr-6">
          <SectionHeading
            title="Evaluations"
            subtitle={`Best scores for ${model.eval_model_slug ?? model.slug}`}
          />
          <div className="mt-5">
            {evals.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : evals.isError ? (
              // "No scores yet" would be a claim about this model; the
              // failure is ours, and saying so is the difference between
              // a gap in the data and a gap in the training.
              <p className="text-sm text-destructive">Failed to load evaluations.</p>
            ) : (
              <EvaluationsPanel groups={evals.data ?? []} />
            )}
          </div>
          {/* Ghost, and pulled left by its own padding so the label lines
              up with the bars above it rather than sitting inset. */}
          <Button variant="ghost" size="sm" asChild className="-ml-3 mt-4">
            <Link to="/evals">
              All evals
              <ArrowRight className="ml-2 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
        <div className="py-6 md:pl-6">
          <SectionHeading title="Deployment" subtitle="Live configuration" />
          <div className="mt-5">
            <DeploymentPanel model={model} routing={fallbackOptions} />
          </div>
        </div>
      </section>

      <ModelHomeSection
        title="Checkpoints"
        subtitle={
          checkpoints.data
            ? lineageSubtitle(checkpoints.data)
            : 'Scored checkpoints, newest first'
        }
      >
        {checkpoints.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : checkpoints.data ? (
          <CheckpointsTable checkpoints={checkpoints.data} />
        ) : (
          <p className="text-sm text-destructive">Failed to load checkpoints.</p>
        )}
      </ModelHomeSection>

      <ModelHomeSection
        title="Activity"
        subtitle="Checkpoint scores, promotions, and gateway incidents"
      >
        {activity.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : activity.data ? (
          <EventsList events={activity.data.events} />
        ) : (
          <p className="text-sm text-destructive">Failed to load activity.</p>
        )}
      </ModelHomeSection>

      {/* The id is the uptime wall's fallback drill-down scroll target. */}
      <div id="request-log">
        <ModelHomeSection
          title={logView === 'requests' ? 'Request log' : 'Conversations'}
          subtitle={
            logView === 'requests'
              ? 'Every gateway call to this model. Click a row with a trace id to see what it sent and got back.'
              : 'Multi-turn agent runs that called this model, most recently active first'
          }
        >
          <RequestsTab
            modelId={model.id}
            view={logView}
            onView={setLogView}
            pageName={model.display_name}
          />
        </ModelHomeSection>
      </div>

      <ChatSheet model={model} open={chatOpen} onOpenChange={setChatOpen} />

      <ModelFormDialog
        mode="edit"
        model={model}
        scope={TEAM_SCOPE}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={() => { toast.success('Model saved') }}
      />

      <DeleteModelDialog
        model={model}
        scope={TEAM_SCOPE}
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onDeleted={() => {
          toast.success('Model deleted')
          navigate('/models')
        }}
      />
    </PageContainer>
  )
}
